import { useParams } from 'react-router-dom';
import { useTenantStore } from '../store/tenantStore';
import { ERP_MODULES } from '../store/moduleStore';

/** Build a path prefixed with the current company slug. */
export function companyPath(slug: string, path: string): string {
  const clean = path.startsWith('/') ? path : `/${path}`;
  return `/${slug}${clean}`;
}

export function useCompanySlug(): string {
  const { companySlug } = useParams<{ companySlug?: string }>();
  const stored = useTenantStore(s => s.companySlug);
  return companySlug || stored || 'default';
}

export function useCompanyPath(path: string): string {
  return companyPath(useCompanySlug(), path);
}

/** Reserved first path segments — not company slugs. */
export const PUBLIC_SEGMENTS = new Set(['login', 'setup', 'modules', 'location', 'print']);

export function stripCompanyPrefix(pathname: string, slug: string): string {
  const prefix = `/${slug}`;
  if (pathname.startsWith(prefix)) return pathname.slice(prefix.length) || '/';
  return pathname;
}

/** Infer active ERP module from a company-relative path (no slug prefix). */
export function inferModuleFromPath(path: string): string | null {
  const p = (path.split('?')[0] || '/').toLowerCase();

  for (const mod of ERP_MODULES) {
    const def = mod.defaultPath.split('?')[0].toLowerCase();
    if (p === def || (def !== '/dashboard' && p.startsWith(def))) return mod.id;
  }

  const rules: [string, string][] = [
    ['/my-space', 'myspace'],
    ['/leads', 'crm'], ['/crm', 'crm'], ['/sales', 'crm'],
    ['/supply-chain', 'supplychain'], ['/procurement', 'supplychain'],
    ['/inventory', 'supplychain'], ['/dealer', 'supplychain'],
    ['/manufacturing', 'manufacturing'], ['/quality', 'manufacturing'],
    ['/finance', 'finance'], ['/tax', 'finance'],
    ['/hr', 'hr'],
    ['/projects', 'projects'],
    ['/helpdesk', 'helpdesk'],
    ['/assets', 'assets'], ['/documents', 'assets'],
    ['/reports', 'reports'],
    ['/approvals', 'approvals'],
    ['/admin', 'admin'],
  ];
  for (const [prefix, id] of rules) {
    if (p === prefix || p.startsWith(`${prefix}/`)) return id;
  }
  return null;
}
